// Pending-sync operation queue.
//
// Operations queued when the server is unreachable (offline or 5xx).
// Stored in the `_sync_queue` IndexedDB object store so the queue survives
// page reloads.
//
// FIFO drain. On success → remove. On failure (network/5xx) → increment
// retries + stash lastError. After MAX_RETRIES failed attempts the op is
// left in the queue but skipped by subsequent automatic drains; it'll be
// retried again when `online` fires, when `drain()` is called by a fresh
// successful mutation, or when the queue is manually drained next page-load.

import type { TkosDb } from "./db";
import { SYNC_QUEUE_STORE } from "./db";

export type SyncAction = "create" | "update" | "delete";

export interface SyncOp {
  /** Distinct from the record id; e.g. `op-<base36ts>-<rand>`. */
  id: string;
  /** Target data store, e.g. `submissions`. */
  store: string;
  /** The record's own id. */
  recordId: string;
  action: SyncAction;
  /** JSON-serialisable payload for create/update; absent for delete. */
  body?: unknown;
  /** Relative URL, e.g. `/portal/submissions` or `/portal/submissions/sub-1`. */
  endpoint: string;
  retries: number;
  lastError?: string;
  /** unix ms */
  createdAt: number;
}

export interface SyncQueue {
  /** Enqueue a pending op. Returns the persisted op with id+retries+createdAt. */
  enqueue(op: Omit<SyncOp, "id" | "retries" | "createdAt">): Promise<SyncOp>;
  /** Read the entire queue (FIFO order). */
  list(): Promise<SyncOp[]>;
  /**
   * Drain the queue against the supplied api function. Idempotent —
   * concurrent calls coalesce to a single in-flight pass.
   */
  drain(
    api: (path: string, init?: RequestInit) => Promise<Response>,
  ): Promise<{ drained: number; failed: number }>;
  /** Any queued ops still pending for a given record. */
  pendingForRecord(store: string, recordId: string): Promise<SyncOp[]>;
  /** Subscribe to queue-size changes. Returns an unsubscribe fn. */
  on(event: "change", fn: () => void): () => void;
}

const MAX_RETRIES = 5;

function makeOpId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `op-${ts}-${rand}`;
}

function methodFor(action: SyncAction): string {
  if (action === "create") return "POST";
  if (action === "update") return "PUT";
  return "DELETE";
}

export function createSyncQueue(db: TkosDb): SyncQueue {
  const listeners = new Set<() => void>();
  let inflight: Promise<{ drained: number; failed: number }> | null = null;

  function emitChange(): void {
    for (const fn of listeners) {
      try {
        fn();
      } catch (err) {
        // Listener errors must not interrupt the queue.
        console.error("[sync-queue] listener threw", err);
      }
    }
  }

  async function enqueue(
    input: Omit<SyncOp, "id" | "retries" | "createdAt">,
  ): Promise<SyncOp> {
    const op: SyncOp = {
      id: makeOpId(),
      retries: 0,
      createdAt: Date.now(),
      ...input,
    };
    await db.put(SYNC_QUEUE_STORE, op);
    emitChange();
    return op;
  }

  async function list(): Promise<SyncOp[]> {
    const ops = await db.getAll<SyncOp>(SYNC_QUEUE_STORE);
    return ops.sort((a, b) => a.createdAt - b.createdAt);
  }

  async function pendingForRecord(
    store: string,
    recordId: string,
  ): Promise<SyncOp[]> {
    const ops = await list();
    return ops.filter((o) => o.store === store && o.recordId === recordId);
  }

  function isRetryable(status: number): boolean {
    // Network failures throw and are caught below; HTTP-wise we retry only on
    // server-side errors. 4xx is a permanent failure — don't loop on bad data.
    return status >= 500 && status <= 599;
  }

  async function drain(
    api: (path: string, init?: RequestInit) => Promise<Response>,
  ): Promise<{ drained: number; failed: number }> {
    if (inflight) return inflight;
    inflight = (async (): Promise<{ drained: number; failed: number }> => {
      let drained = 0;
      let failed = 0;
      try {
        const ops = await list();
        for (const op of ops) {
          // Skip ops that have exhausted the auto-retry budget. They stay in
          // the queue and will be picked up the next time `drain` is invoked
          // by an `online` event / successful mutation / page load.
          if (op.retries >= MAX_RETRIES) {
            failed += 1;
            continue;
          }

          const init: RequestInit = { method: methodFor(op.action) };
          if (op.body !== undefined) {
            init.body = JSON.stringify(op.body);
          }
          try {
            const res = await api(op.endpoint, init);
            if (res.ok) {
              await db.remove(SYNC_QUEUE_STORE, op.id);
              drained += 1;
              continue;
            }
            if (!isRetryable(res.status)) {
              // 4xx: drop — the server rejected the payload, retrying won't
              // help. Surface to console so devs can see what was dropped.
              console.warn(
                `[sync-queue] dropping op ${op.id}: HTTP ${res.status}`,
              );
              await db.remove(SYNC_QUEUE_STORE, op.id);
              drained += 1;
              continue;
            }
            await db.put(SYNC_QUEUE_STORE, {
              ...op,
              retries: op.retries + 1,
              lastError: `HTTP ${res.status}`,
            });
            failed += 1;
          } catch (err) {
            const msg =
              err instanceof Error ? err.message : String(err);
            await db.put(SYNC_QUEUE_STORE, {
              ...op,
              retries: op.retries + 1,
              lastError: msg,
            });
            failed += 1;
          }
        }
      } finally {
        emitChange();
        inflight = null;
      }
      return { drained, failed };
    })();
    return inflight;
  }

  function on(event: "change", fn: () => void): () => void {
    if (event !== "change") return (): void => {};
    listeners.add(fn);
    return (): void => {
      listeners.delete(fn);
    };
  }

  return { enqueue, list, drain, pendingForRecord, on };
}
